import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormGroup,
    Checkbox,
    FormControlLabel,
    TextField,
} from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import * as companyService from "services/company-service";

interface IProps {
    open: boolean;
    company: Company | null;
    handleClose: () => void;
    refresh: () => void;
    tenantId?: string;
}

interface ISelectedChilds {
    selected: boolean;
    id: string;
}

const EditModal = (props: IProps) => {
    const { open, company, handleClose, refresh, tenantId } = props;
    const { t } = useTranslation();
    const [companyName, setCompanyName] = React.useState<string>(
        company?.name || "",
    );
    const [companyValidNames, setCompanyValidNames] = React.useState<string[]>(
        company?.validNames || [],
    );
    const [error, setError] = React.useState<{
        error: boolean;
        message: string;
    }>({ error: false, message: "" });

    const [selectedChilds, setSelectedChilds] = React.useState<
        ISelectedChilds[]
    >([]);

    const [isLoading, setIsLoading] = React.useState<boolean>(false);

    React.useEffect(() => {
        setError({ error: false, message: "" });
        if (company !== null) {
            setCompanyName(company?.name);
            setCompanyValidNames(
                company?.validNames ? company?.validNames : [],
            );
            setSelectedChilds(
                company.children.map((c) => ({ selected: true, id: c.id })),
            );
        } else {
            setCompanyName("");
            setCompanyValidNames([]);
        }
    }, [company]);

    const isSelectedChild = (id: string): boolean => {
        const found = selectedChilds.find((c) => c.id === id);
        return found !== undefined ? found.selected : false;
    };

    const toggleSelectedChild = (id: string): void => {
        setSelectedChilds((prev) =>
            prev.map((c) =>
                c.id === id ? { ...c, selected: !c.selected } : c,
            ),
        );
    };

    const handleEdit = (): void => {
        console.log(companyName, companyValidNames);
        var validNames = companyValidNames.reduce((acc, curr) => {
            const splitedValues = curr.split(",");
            for (const value of splitedValues) {
                acc.push(value);
            }
            return acc;
        }, [] as string[]);
        if (companyName === "" && validNames.length <= 0) {
            console.log("edit failed");
            return;
        }
        setIsLoading(true);
        companyService
            .update({
                id: company?.id as string,
                name: companyName,
                childCompanyIds: selectedChilds
                    .filter((c) => c.selected)
                    .map((c) => c.id),
            }, tenantId)
            .then(() => {
                handleClose();
                refresh();
                setIsLoading(false);
            })
            .catch((err) => {
                const {
                    response: {
                        data: {
                            error: { message },
                        },
                    },
                } = err;
                console.log(message);
                setError({
                    error: true,
                    message: err.response.data.error.message,
                });
                setIsLoading(false);
            });
    };

    const handleDelete = (): void => {
        console.log("delete", company);
        if (company !== null && company.id !== undefined) {
            companyService
                .deleteCompany(company.id, tenantId)
                .then(() => {
                    handleClose();
                    refresh();
                })
                .catch((err) => {
                    const {
                        response: {
                            data: {
                                error: { message },
                            },
                        },
                    } = err;
                    setError({
                        error: true,
                        message: message,
                    });
                });
        }
    };

    const handleValidNamesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setCompanyValidNames(e.target.value.split(", "));
    };

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth={"sm"}>
            <DialogTitle>{t("company.edit.title")}</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    {error.error && (
                        <Box color="error.main">{t(error.message)}</Box>
                    )}
                </DialogContentText>
                <Box
                    sx={{
                        paddingTop: "0.50rem",
                        paddingBottom: "1rem",
                        height: "fit-content",
                    }}
                >
                    <TextField
                        label={t("company.labels.name")}
                        value={companyName}
                        onChange={(e) => setCompanyName(e.target.value)}
                        size="small"
                        sx={{ width: "100%" }}
                    />
                </Box>
                <Box>
                    <FormGroup>
                        {company !== null &&
                            company !== undefined &&
                            company?.children.map((c) => (
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={isSelectedChild(c.id)}
                                            onChange={() =>
                                                toggleSelectedChild(c.id)
                                            }
                                        />
                                    }
                                    label={c.name}
                                />
                            ))}
                    </FormGroup>
                    {/*<TextField
                        label={t("company.labels.validNames")}
                        value={companyValidNames.join(", ")}
                        onChange={handleValidNamesChange}
                        size="small"
                        sx={{ width: "100%" }}
                        helperText={t("company.edit.helper")}
                    />*/}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleClose}
                >
                    {t("company.edit.cancel")}
                </Button>
                <Button
                    variant="contained"
                    color="error"
                    onClick={handleDelete}
                >
                    {" "}
                    {t("company.edit.delete")}
                </Button>
                {!isLoading && (
                    <Button
                        variant="contained"
                        color="success"
                        onClick={handleEdit}
                    >
                        {" "}
                        {t("company.edit.update")}
                    </Button>
                )}
                {isLoading && (
                    <CircularProgress
                        size="30px"
                        sx={{ marginLeft: "1 rem" }}
                    />
                )}
            </DialogActions>
        </Dialog>
    );
};

export default EditModal;
